import { useMemo } from "react";
import i18n from "locales/i18n";
import { createFormatters } from "locales/format";
import moment from "moment";
import {
  RequestBookingInput,
  useGetQuotesByAccountQuery,
  useRequestBookingMutation,
  useVendorAvailabilityForQuoteQuery,
} from "generated/graphql";
import { toAvailabilityData } from "../../../setAvailability/availabilityApi";
import { AvailabilityData } from "../../../setAvailability/types";
import { EVENT_TYPES, BookingType } from "../../../eventTypes";
import { Booking } from "../../types";

// Appointments are requested in fixed 30-minute slots (mirrors the server's
// validation in ev-server/src/booking/slots.ts).

const tc = i18n.getFixedT(null, "calendar");
export const REQUEST_SLOT_MINUTES = 30;

// Customer-facing labels for the types a customer can see, relabelled from the
// vendor's wording ("Customer Call" etc.). "onsite" is included because a
// vendor can still invite a customer to a site visit, even though the customer
// can't request one.
export const CUSTOMER_TYPE_LABELS = Object.defineProperties(
  {},
  Object.fromEntries(
    (["video", "call", "onsite"] as const).map((type) => [
      type,
      { get: () => tc(`customer_types.${type}`), enumerable: true },
    ]),
  ),
) as Partial<Record<BookingType, string>>;

// Types a customer can request — remote only. Site visits are scheduled by the
// vendor (as are the vendor-internal "blocked" / "inspection" types), and the
// server enforces the same list in requestBookingSchema.
export const CUSTOMER_REQUEST_TYPES = EVENT_TYPES.filter((t) =>
  ["video", "call"].includes(t.id),
).map((t) => ({ ...t, label: CUSTOMER_TYPE_LABELS[t.id] ?? t.label }));

// A quote the appointment can be linked to.
export interface BookableQuote {
  quoteId: number;
  label: string;
  /** Epoch milliseconds, as the API returns it. */
  expiresAt?: string | null;
}

// A vendor the user can book with, and the quotes they've sent the user.
export interface BookableVendor {
  vendorId: number;
  vendorName: string;
  quotes: BookableQuote[];
}

/**
 * Last day an appointment may be booked for: a visit held after the estimate
 * expires would be against pricing that has lapsed, and the server rejects it.
 * Undefined when the quote carries no expiry.
 */
export const quoteMaxDate = (
  quote?: { expiresAt?: string | null } | null,
): string | undefined => {
  const expiry = quote?.expiresAt ? Number(quote.expiresAt) : NaN;

  return Number.isNaN(expiry) ? undefined : moment(expiry).format("YYYY-MM-DD");
};

export const quoteLabel = (q: {
  id: number;
  estimateNumber?: string | null;
  chargerModel?: string | null;
  totalCost?: number | null;
}): string => {
  const name = q.estimateNumber
    ? `#${q.estimateNumber}`
    : tc("quote_label", { id: q.id });
  const model = q.chargerModel ? ` — ${q.chargerModel}` : "";
  const cost =
    q.totalCost != null
      ? ` (${createFormatters(i18n.language).money(q.totalCost)})`
      : "";
  return `${name}${model}${cost}`;
};

// The vendors a user can book with — one entry per vendor, with all of that
// vendor's quotes to the user (most recent first).
export const useBookableVendors = (enabled: boolean): BookableVendor[] => {
  const [{ data }] = useGetQuotesByAccountQuery({ pause: !enabled });

  return useMemo(() => {
    const byVendor = new Map<number, BookableVendor>();
    const rows = [...(data?.getQuotesByAccount ?? [])].sort((a, b) =>
      (b.updatedAt ?? b.createdAt ?? "").localeCompare(
        a.updatedAt ?? a.createdAt ?? "",
      ),
    );
    rows.forEach((q) => {
      const vendor = q.evChargerService?.vendor;
      // Expired quotes aren't bookable (the server rejects them too), so they
      // never reach the picker — a vendor with only expired quotes drops out.
      if (!vendor || q.isExpired) {
        return;
      }
      const entry = byVendor.get(vendor.id) ?? {
        vendorId: vendor.id,
        vendorName: vendor.businessName,
        quotes: [],
      };
      entry.quotes.push({
        quoteId: q.id,
        label: quoteLabel(q),
        expiresAt: q.expiresAt,
      });
      byVendor.set(vendor.id, entry);
    });
    return Array.from(byVendor.values());
  }, [data]);
};

/**
 * Availability of the vendor behind one of the user's quotes, plus the
 * vendor's occupied ranges as minimal Booking stubs so the slot picker can
 * block them out. Pass `undefined` while no quote is selected yet.
 */
export const useQuoteVendorAvailability = (quoteId: number | undefined) => {
  const [{ data, fetching }] = useVendorAvailabilityForQuoteQuery({
    variables: { quoteId: quoteId as number },
    pause: quoteId === undefined,
    requestPolicy: "cache-and-network",
  });

  const payload = data?.vendorAvailabilityForQuote;

  const availability: AvailabilityData | null = useMemo(
    () =>
      payload?.availability ? toAvailabilityData(payload.availability) : null,
    [payload],
  );

  const busyBookings: Booking[] = useMemo(
    () =>
      (payload?.busySlots ?? []).map((s, i) => ({
        id: `busy-${i}`,
        title: "",
        type: "call" as BookingType,
        date: s.date,
        from: s.fromTime,
        to: s.toTime,
        allDay: s.allDay,
        attendees: [],
        attachments: [],
      })),
    [payload],
  );

  return {
    loading: quoteId !== undefined && fetching && !payload,
    availabilityConfigured: payload?.availabilityConfigured ?? false,
    availability,
    busyBookings,
  };
};

export interface RequestBookingResult {
  ok: boolean;
  error?: string;
}

export const useRequestBooking = () => {
  const [{ fetching }, requestBookingMut] = useRequestBookingMutation();

  const request = async (
    input: RequestBookingInput,
  ): Promise<RequestBookingResult> => {
    const res = await requestBookingMut({ input });
    const payload = res.data?.requestBooking;
    if (res.error || (payload?.errors && payload.errors.length > 0)) {
      return {
        ok: false,
        error:
          payload?.errors?.[0]?.message ??
          res.error?.message ??
          tc("snack.request_failed"),
      };
    }
    return { ok: true };
  };

  return { request, requesting: fetching };
};
